/** @jsxImportSource @emotion/react */
import { useState } from 'react'

import type { Theme, css } from '@emotion/react'
import { TextButton } from '@twistopayments/design-system/components/atoms/button'
import { Input } from '@twistopayments/design-system/components/atoms/input'
import { Loader } from '@twistopayments/design-system/components/atoms/loader'
import { Typography } from '@twistopayments/design-system/components/atoms/typography'

import type {
  FormSimpleInputComponent,
  LinksSimpleButtonComponent,
} from '@twisto/api-cms'

import * as styles from './form.styles'

type FormProps = {
  inputs?: FormSimpleInputComponent[]
  submit?: LinksSimpleButtonComponent
  name?: string
  formStyles?: (theme: Theme) => ReturnType<typeof css>
  inputsContainerStyles?: (theme: Theme) => ReturnType<typeof css>
  onSubmit?: (formData: Record<string, string>) => void
  submitType: 'link' | 'button'
  error?: boolean
  errorMsg?: string
  loading?: boolean
}

export const Form = ({
  inputs,
  submit,
  name,
  formStyles,
  onSubmit,
  submitType,
  error,
  errorMsg,
  loading,
  inputsContainerStyles,
}: FormProps) => {
  const [formValues, setFormValues] = useState<Record<string, string>>({})

  if (!submit || !onSubmit) {
    return null
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormValues((prev) => ({ ...prev, [name]: value }))
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSubmit(formValues)
  }

  return (
    <form css={formStyles} onSubmit={handleSubmit}>
      <Typography fontWeight="heavy" variant="h2">
        {name}
      </Typography>
      <div css={inputsContainerStyles || styles.inputs}>
        {loading && <Loader />}

        {inputs?.map((input) => (
          <Input
            key={input.id}
            error={Boolean(error)}
            name={input.name ?? ''}
            placeholder={input.placeholder ?? ''}
            value={formValues[input.name ?? ''] ?? ''}
            onChange={handleChange}
          />
        ))}
        {error && errorMsg && (
          <Typography
            align="start"
            color="error"
            css={styles.formError}
            variant="b3"
          >
            {errorMsg}
          </Typography>
        )}
      </div>

      <TextButton
        href={submitType === 'link' ? submit.link : undefined}
        type="submit"
      >
        {submit.text}
      </TextButton>
    </form>
  )
}
